import Head from 'next/head'
import { useRouter } from "next/router";
import { SEOProps } from '../../lib/utils/seo'

function getCanonicalURL(path: string) {
  const CANONICAL_DOMAIN = 'https://myphysiowave.gr';

  const _pathSliceLength = Math.min.apply(Math, [
      path.indexOf('?') > 0 ? path.indexOf('?') : path.length,
      path.indexOf('#') > 0 ? path.indexOf('#') : path.length
  ]);
  const canonicalURL= CANONICAL_DOMAIN + path.substring(0, _pathSliceLength);
  return canonicalURL
}

export default function Meta(props: SEOProps) {
  const router = useRouter();
  // the locale-free path: Next strips the locale prefix from asPath only for
  // the default locale, so /en/... must be normalised by hand
  const basePath = router.asPath.replace(/^\/en(?=\/|$)/, "") || "/";

  return (
    <Head>
      <title>{props.pageTitle}</title>

      {/* hreflang, driven by the configured locale list (en disabled
          2026-09-09 — a single-locale site emits no alternates; they come
          back with the locale). x-default points at Greek — the clinic's
          primary audience and the default locale. */}
      {/* no fragment here: next/head drops children wrapped in one */}
      {(router.locales ?? []).length > 1 ? (
        <link rel="alternate" hrefLang="el" href={getCanonicalURL(basePath)} />
      ) : null}
      {(router.locales ?? []).length > 1 ? (
        <link rel="alternate" hrefLang="en" href={getCanonicalURL("/en" + (basePath === "/" ? "" : basePath))} />
      ) : null}
      {(router.locales ?? []).length > 1 ? (
        <link rel="alternate" hrefLang="x-default" href={getCanonicalURL(basePath)} />
      ) : null}
      <meta name="description" content={props.description} />
      {/* shared.seo metaRobots — set only where an admin (or seed) opted a
          page out of the index; no tag means indexable */}
      {props.metaRobots ? <meta name="robots" content={props.metaRobots} /> : null}

      <meta name="theme-color" content="#ffffff" />    
      <link rel="icon" href="/favicon.ico" sizes="any"/>
      {/*<link rel="icon" href="/favicon.svg" type="image/svg+xml"/>*/}
      <link rel="apple-touch-icon" href="/apple-touch-icon.png"/>
      <link rel="manifest" href="/site.webmanifest"/>

      {/* self-canonical unless shared.seo canonicalURL overrides (e.g. a
          page that intentionally mirrors another's content) */}
      <link rel="canonical" href={props.canonicalUrl || getCanonicalURL(router.asPath)} />
      {/* shared.seo structuredData — admin-authored JSON-LD passthrough */}
      {props.structuredData ? (
        <script type="application/ld+json"
          // eslint-disable-next-line react/no-danger
          dangerouslySetInnerHTML={{ __html: JSON.stringify(props.structuredData) }} />
      ) : null}

      <meta property="og:title" content={props.openGraphTitle} />
      <meta property="og:site_name" content={props.openGraphWebsiteTitle} />
      <meta property="og:description" content={props.openGraphDescription} />
      <meta property="og:locale" content={props.openGraphLocale} />
      <meta property="og:type" content={props.openGraphType} />
      <meta property="og:image" content={props.openGraphImageUrl} />
      <meta property="og:image:secure_url" content={props.openGraphImageUrl} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:url" content={props.canonicalUrl || getCanonicalURL(router.asPath)} />
      {/* Default Facebook App Id for all apps/websites. Simply used to remove the warning from the share debugger */}
      {/* https://yoast.com/help/fb-app-id-warnings/ */}
      <meta property="fb:app_id" content="966242223397117" />
    </Head>
    )
  }